/* RESPONSIBLE TEAM: team-pricing-and-packaging */
import { A } from '@ember/array';
import { action } from '@ember/object';
import { assign } from '@ember/polyfills';
import { inject as service } from '@ember/service';
import { isPresent } from '@ember/utils';
import { confirmSubscriptionChanges as confirmSubscriptionChangesFromInboxSeats } from 'embercom/lib/admins/inbox-seat-info';
import {
  confirmMultipleSeatOveragesIncrease,
  confirmSeatOveragesForSelfServeAnnualInvite,
} from 'embercom/lib/admins/multiple-seats-info';
import { confirmPriceIncrease as confirmPriceIncreaseFromSeats } from 'embercom/lib/admins/seat-info';
import { post } from 'embercom/lib/ajax';
import {
  INVITE_USAGE as INVITE,
  ANNUAL_BILLING_PERIOD,
  MONTHLY_BILLING_PERIOD,
} from 'embercom/lib/settings/seats/constants';
import ManageTeammatesRoute from 'embercom/routes/apps/app/settings/base/manage-teammates-route';
import { DEFAULT_LOCALE } from 'embercom/services/intl';
import {
  COPILOT_SEAT_TYPE as COPILOT,
  PRICING_5_X_FULL_SEAT_TYPE as FULL,
  UNASSIGNABLE_SEAT_TYPES as UNASSIGNABLE_SEAT_TYPES,
} from 'embercom/lib/settings/seats/constants';
import { FIN_AI_COPILOT_ID } from 'embercom/lib/billing';
import Metrics from 'embercom/models/metrics';

const METRICS_NAMESPACE = 'Invite.PermissionsRoute';

export default class PermissionsRoute extends ManageTeammatesRoute {
  @service appService;
  @service store;
  @service router;
  @service intl;
  @service permissionsHierarchyService;
  @service notificationsService;
  @service intercomEventService;
  @service cardlessTrialService;
  @service intercomConfirmService;
  @service guideLibraryService;

  coreSeatBillingPeriodSelected = MONTHLY_BILLING_PERIOD;
  fullSeatPriceForBillingPeriod = {};
  sentInvites = false;

  analytics = {
    section: 'settings',
    place: 'teammate-invite-permissions',
  };

  initialUsage;
  updatedUsage;

  async beforeModel() {
    await super.beforeModel(...arguments);
    await this.permissionsHierarchyService.ensureDataIsLoaded.perform();

    if (!this.modelFor('apps.app.settings.workspace.teammates.invite.new')) {
      this.router.transitionTo('apps.app.settings.workspace.teammates.invite.new');
    }
  }

  _getPermissionsObject() {
    return this.store.createRecord('permission', {
      app_id: this.appService.app.id,
    });
  }

  model() {
    return {
      app: this.appService.app,
      emails: this.modelFor('apps.app.settings.workspace.teammates.invite.new').emails,
      primaryTeams: this.modelFor('apps.app.settings.workspace.teammates.invite.new').primaryTeams,
      secondaryTeams: this.modelFor('apps.app.settings.workspace.teammates.invite.new')
        .secondaryTeams,
      permissionsObject: this._getPermissionsObject(),
      seatTypes: this._seatTypes(),
      inviteLocale: this._getLocaleFromLocalStorage(),
      roles: this.modelFor('apps.app.settings.workspace.teammates.invite.new').roles,
      copilotData: this.modelFor('apps.app.settings.workspace.teammates.index')?.copilotData,
      usageType: INVITE,
    };
  }

  _seatTypes() {
    let seatTypes = A();

    if (this.appService.app.onFinForPlatformsOrPricing5) {
      seatTypes.pushObject(FULL);
    }

    if (
      this._copilotOnTrial() &&
      !this.appService.app.canUsePerProductPricingFlow &&
      !this.appService.app.hasValueBasedPricing2
    ) {
      seatTypes.pushObject(COPILOT);
    }

    return seatTypes;
  }

  _copilotOnTrial() {
    let product = this.appService.app.products.find((product) => product.id === FIN_AI_COPILOT_ID);

    return product?.isOnTrial;
  }

  @action
  setCoreSeatBillingPeriodSelected(billingPeriod, fullSeatPriceForBillingPeriod) {
    this.coreSeatBillingPeriodSelected = billingPeriod;
    this.fullSeatPriceForBillingPeriod = fullSeatPriceForBillingPeriod;
  }

  afterModel(model) {
    if (model.emails.length === 0) {
      this.transitionTo('apps.app.settings.workspace.teammates.invite.new');
    }
  }

  _hasInboxAccessAcrossPricingPlans() {
    if (this.currentModel.permissionsObject.has_inbox_access) {
      return true;
    }

    if (this.currentModel.seatTypes.length === 0) {
      return false;
    }

    return !this.currentModel.seatTypes.some((seatType) =>
      UNASSIGNABLE_SEAT_TYPES.includes(seatType),
    );
  }

  shouldIncludeConfiguration() {
    if (!this._hasInboxAccessAcrossPricingPlans()) {
      return false;
    }

    let primaryTeamsSelected =
      this.currentModel.primaryTeams && this.currentModel.primaryTeams.length > 0;
    let secondaryTeamsSelected =
      this.currentModel.secondaryTeams && this.currentModel.secondaryTeams.length > 0;
    return primaryTeamsSelected || secondaryTeamsSelected;
  }

  teamIdsWithPriorityLevel(teamIds, priorityLevel) {
    return teamIds.map((teamId) => {
      return {
        id: teamId,
        priority_level: priorityLevel,
      };
    });
  }

  async sendInvites() {
    try {
      let permissions = this.currentModel.permissionsObject.serialize();

      let shouldIncludeConfig = this.shouldIncludeConfiguration();

      if (!this._hasInboxAccessAcrossPricingPlans()) {
        Metrics.capture({
          increment: [`${METRICS_NAMESPACE}.dropped_configuration_due_to_no_inbox_access`],
        });
      }

      let invitees = this.currentModel.emails.map((email) => {
        let inviteeData = {
          email,
          seat_types: this.currentModel.seatTypes.toArray(),
          locale: this.currentModel.inviteLocale,
        };

        if (shouldIncludeConfig) {
          inviteeData.configuration = {
            teams: [
              ...this.teamIdsWithPriorityLevel(this.currentModel.primaryTeams, 1),
              ...this.teamIdsWithPriorityLevel(this.currentModel.secondaryTeams, 2),
            ],
          };
        }

        return assign(inviteeData, permissions);
      });

      await post('/ember/invite_admin_groups', { invitees, app_id: this.appService.app.id });

      this._setLocaleInLocalStorage(this.currentModel.inviteLocale);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'created',
        object: 'teammate_invite',
        place: 'teammate-list',
        numberOfInvites: invitees.length,
      });
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.teammates.invite.notification.invite-success', {
          inviteCount: invitees.length,
          appName: this.appService.app.name,
        }),
      );
      let transition = this.transitionTo('apps.app.settings.workspace.teammates');
      transition.data.selectedTab = 'invites';
    } catch (error) {
      if (error.jqXHR?.responseJSON?.error_code === 'seat_limit_reached') {
        this.notificationsService.notifyErrorWithButton(
          this.intl.t('pricing-and-packaging.multi_workspace_seat_limit_reached.error_message'),
          {
            label: this.intl.t(
              'pricing-and-packaging.multi_workspace_seat_limit_reached.button_label',
            ),
            action: this.redirectToBilling.bind(this),
          },
          10000,
        );
      } else if (
        this.appService.app.cannotAssignWiderAccess &&
        isPresent(error?.jqXHR?.responseJSON?.tokens)
      ) {
        this.notificationsService.notifyError(
          this.intl.t(
            'settings.error_message.cannot_modify_unowned_permissions_invite_creation_error',
          ),
        );
      } else if (error.jqXHR?.responseJSON?.error_code === 'invite_limit_reached') {
        this.notificationsService.notifyError(
          this.intl.t('settings.error_message.cannot_send_invites_limit_error'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('settings.error_message.cannot_send_invites_error'),
        );
      }
      console.error(error);
    }
    this.sentInvites = true;
  }

  redirectToBilling() {
    this.router.transitionTo('apps.app.billing');
  }

  get numberOfInboxSeatsRequired() {
    return this.currentModel.permissionsObject.has_inbox_access
      ? this.currentModel.emails.length
      : 0;
  }

  get numberOfTeammateSeatsRequired() {
    return this.appService.app.canSeeCostChangeCard ? this.currentModel.emails.length : 0;
  }

  get numberOfMultipleSeatTeammateInvitesRequired() {
    return this.appService.app.hasMultipleSeatTypes ? this.currentModel.emails.length : 0;
  }

  @action
  setInitialUsage(usage) {
    this.initialUsage = usage;
  }

  @action
  setUpdatedUsage(usage) {
    this.updatedUsage = usage;
  }

  async confirmChanges() {
    this.sentInvites = true;
    return (
      (await confirmSubscriptionChangesFromInboxSeats(
        this.numberOfInboxSeatsRequired,
        this.showConfirmModal,
        {},
        true,
        this.currentModel.seatTypes.includes(COPILOT),
        this.currentModel.copilotData,
      )) &&
      (await confirmPriceIncreaseFromSeats(
        this.numberOfTeammateSeatsRequired,
        this.currentModel.seatTypes.includes(COPILOT),
        this.showConfirmModal,
      )) &&
      (await this.confirmMultipleSeatOveragesIncreaseIfHasSeats())
    );
  }

  async confirmMultipleSeatOveragesIncreaseIfHasSeats() {
    let onNonPricing5FreeTrial =
      this.cardlessTrialService.inTrialOrExpiredTrial &&
      !this.appService.app.onFinForPlatformsOrPricing5;

    if (this.coreSeatBillingPeriodSelected === ANNUAL_BILLING_PERIOD) {
      await confirmSeatOveragesForSelfServeAnnualInvite(
        this.initialUsage,
        this.updatedUsage,
        this.fullSeatPriceForBillingPeriod,
        this.router,
        this.appService.app.id,
      );

      return false;
    } else if (this.appService.app.hasMultipleSeatTypes && !onNonPricing5FreeTrial) {
      return await confirmMultipleSeatOveragesIncrease(
        this.appService.app,
        {
          seatTypes: this.currentModel.seatTypes,
          initialUsage: this.initialUsage,
          updatedUsage: this.updatedUsage,
          teammateCount: this.currentModel.emails.length,
          isInvite: true,
          haveSeatsChanged: true,
          copilotSeatChanged: this.currentModel.seatTypes.includes(COPILOT),
        },
        this.showConfirmModal,
      );
    }
    return true;
  }

  @action
  async confirmAndSendInvites() {
    try {
      this.currentModel.permissionsObject.validate();
      if (await this.confirmChanges()) {
        await this.sendInvites();
        if (this.guideLibraryService.canUseGuideLibraryService) {
          await this.guideLibraryService.markStepCompleted(
            'guide_library_foundational_steps_invite_teammates',
          );
        }
      }
    } catch (error) {
      this.notificationsService.notifyError(error.message);
    }
    this.sentInvites = true;
  }

  @action
  async showConfirmModal(options) {
    return this.intercomConfirmService.confirm(options);
  }

  _getLocaleFromLocalStorage() {
    let storedLocale = localStorage.getItem('localeForInvite');
    if (storedLocale && this.intl.supportedLocales.includes(storedLocale)) {
      return storedLocale;
    }
    return DEFAULT_LOCALE;
  }

  _setLocaleInLocalStorage(newLocale) {
    localStorage.setItem('localeForInvite', newLocale);
  }
  @action
  async willTransition(transition) {
    if (this.sentInvites) {
      return true;
    }

    transition.abort();

    let confirmOptions = {
      title: this.intl.t('settings.teammates.invite.cancel-modal.close-without-saving'),
      primaryButtonType: 'primary-destructive',
      confirmButtonText: this.intl.t(
        'settings.teammates.invite.cancel-modal.close-without-saving-btn',
      ),
      cancelButtonText: this.intl.t('settings.teammates.invite.cancel-modal.keep-editing'),
      body: this.intl.t('settings.teammates.invite.cancel-modal.lose-your-changes'),
    };

    if (await this.intercomConfirmService.confirm(confirmOptions)) {
      this.sentInvites = true;
      this.router.transitionTo('apps.app.settings.workspace.teammates');
    }
  }
}
